import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LayoutWrapper from '../../../../shared/components/LayoutWrapper';
import SmartTable, { SmartTableColumn } from '../../../../shared/components/SmartTable';
import SmartTableButtons from '../../../../shared/components/SmartTableButtons';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { API_CONFIG, API_ENDPOINTS } from '../../../../config/api';

interface Receipt {
  Oid: string;
  CustomerID?: string;
  CustomerName?: string;
  Number?: string;
  BillId?: string;
  Date?: string;
  YearType?: number;
  PeriodType?: number;
  BusinessCustomerOid?: string;
  BusinessCustomerRd?: string;
  BusinessCustomerName?: string;
  CityTaxSalesAmount?: number;
  VATPayableAmount?: number;
  NonVATSalesAmount?: number;
  VATInclusiveSalesAmount?: number;
  ReceiptType?: string;
  FromType?: string;
  IsLedger?: boolean;
  CreateDate?: string;
  [key: string]: any;
}

interface Customer {
  Oid: string;
  CustomerID: string;
  CustomerName: string;
  [key: string]: any;
}

const formatNumber = (value: any) => {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value: any) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const renderWithLedgerHighlight = (key: string, formatter?: (v: any) => any) => (value: any, row: any) => {
  let display = formatter ? formatter(value) : value;
  if (!formatter && key.toLowerCase().includes('date')) {
    display = formatDate(value);
  }
  if (display === null || display === undefined || display === '') display = '-';
  if (row?.IsLedger) {
    return <span style={{ color: '#10B981', fontWeight: 600 }}>{display}</span>;
  }
  return display;
};

const SalesReceiptsPage: React.FC = () => {
  const { userAuth } = useAuth();
  
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState('2025');
  const [period, setPeriod] = useState('11');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  // Customer list state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerOid, setSelectedCustomerOid] = useState<string>('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  
  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError(null);
      
    try {
      const token = localStorage.getItem('novaqToken');
      if (!token) {
        throw new Error('NovaQ token олдсонгүй. Дахин нэвтэрнэ үү.');
      }

      const queryYear = year || '';
      const queryPeriod = period || '';
      const url = `${API_CONFIG.BASE_URL}/api/taxsystem/ebarimt/accountant/receipts/sales?year=${encodeURIComponent(queryYear)}&period=${encodeURIComponent(queryPeriod)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Серверийн алдаа: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success) {
        setReceipts(result.data || []);
      } else {
        throw new Error(result?.message || 'Өгөгдөл татахад алдаа гарлаа');
      }
    } catch (err: any) {
      console.error('❌ Sales receipts fetch error:', err);
      setError(err?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  }, [year, period]);

  // Fetch customers list
  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const token = localStorage.getItem('novaqToken');
      if (!token) {
        throw new Error('NovaQ token олдсонгүй. Дахин нэвтэрнэ үү.');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.CUSTOMERS.BASE}/accountant/customers`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Серверийн алдаа: ${response.status}`);
      }

      const result = await response.json();

      let customersList: Customer[] = [];
      if (result?.success && result?.data) {
        customersList = result.data;
      } else if (Array.isArray(result)) {
        customersList = result;
      } else {
        throw new Error(result?.message || 'Харилцагчдын жагсаалт татахад алдаа гарлаа');
      }

      // CustomerName-аар эрэмбэлэх
      customersList.sort((a, b) => {
        const nameA = (a.CustomerName || '').toLowerCase();
        const nameB = (b.CustomerName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setCustomers(customersList);
    } catch (err: any) {
      console.error('❌ Customers fetch error:', err);
      setError(err?.message || 'Харилцагчдын жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showCustomerDropdown && !target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCustomerDropdown]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm.trim()) {
      // Show all customers when search is empty, but limit to first 50 for performance
      return customers.slice(0, 50);
    }
    const searchLower = customerSearchTerm.toLowerCase();
    return customers.filter(c => 
      (c.CustomerName || '').toLowerCase().includes(searchLower) ||
      (c.CustomerID || '').toString().toLowerCase().includes(searchLower)
    );
  }, [customers, customerSearchTerm]);

  // Get selected customer name for display
  const selectedCustomerName = useMemo(() => {
    if (!selectedCustomerOid) return '';
    const customer = customers.find(c => c.Oid === selectedCustomerOid);
    return customer ? `${customer.CustomerName} (${customer.CustomerID})` : '';
  }, [customers, selectedCustomerOid]);

  const filteredReceipts = useMemo(() => {
    const idTerm = customerIdFilter.trim().toLowerCase();
    const nameTerm = customerNameFilter.trim().toLowerCase();
    const selectedCustomer = selectedCustomerOid ? customers.find(c => c.Oid === selectedCustomerOid) : null;
    
    return receipts.filter(r => {
      const idMatch = idTerm ? (r.CustomerID || '').toString().toLowerCase().includes(idTerm) : true;
      const nameMatch = nameTerm ? (r.CustomerName || '').toLowerCase().includes(nameTerm) : true;
      // Filter by selected customer if one is selected
      const customerMatch = selectedCustomer ? r.CustomerID === selectedCustomer.CustomerID : true;
      return idMatch && nameMatch && customerMatch;
    });
  }, [receipts, customerIdFilter, customerNameFilter, selectedCustomerOid, customers]);

  const footerData = useMemo(() => {
    const sum = (key: keyof Receipt) =>
      filteredReceipts.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

    return {
      CityTaxSalesAmount: sum('CityTaxSalesAmount'),
      VATPayableAmount: sum('VATPayableAmount'),
      NonVATSalesAmount: sum('NonVATSalesAmount'),
      VATInclusiveSalesAmount: sum('VATInclusiveSalesAmount'),
    } as Receipt;
  }, [filteredReceipts]);

  const columns: SmartTableColumn[] = useMemo(
    () => [
      { key: 'CustomerID', label: 'Харилцагчийн регистр', width: 160, render: renderWithLedgerHighlight('CustomerID') },
      { key: 'CustomerName', label: 'Харилцагчийн нэр', width: 200, render: renderWithLedgerHighlight('CustomerName') },
      { key: 'Number', label: 'Баримтын дугаар', width: 150, render: renderWithLedgerHighlight('Number') },
      { key: 'BillId', label: 'BillId', width: 140, render: renderWithLedgerHighlight('BillId') },
      { key: 'Date', label: 'Огноо', width: 140, render: renderWithLedgerHighlight('Date', formatDate) },
      { key: 'DayType', label: 'Өдөр', width: 80, render: renderWithLedgerHighlight('DayType') },
      { key: 'YearType', label: 'Он', width: 80, render: renderWithLedgerHighlight('YearType') },
      { key: 'PeriodType', label: 'Үе', width: 80, render: renderWithLedgerHighlight('PeriodType') },
      { key: 'BusinessCustomerOid', label: 'Бизнес харилцагч', width: 160, render: renderWithLedgerHighlight('BusinessCustomerOid') },
      { key: 'BusinessCustomerName', label: 'Бизнес нэр', width: 160, render: renderWithLedgerHighlight('BusinessCustomerName') },
      { key: 'BusinessCustomerRd', label: 'Бизнес РД', width: 140, render: renderWithLedgerHighlight('BusinessCustomerRd') },
      { key: 'ReceiptType', label: 'Төрөл', width: 120, render: renderWithLedgerHighlight('ReceiptType') },
      { key: 'FromType', label: 'Эх сурвалж', width: 120, render: renderWithLedgerHighlight('FromType') },
      { key: 'CityTaxSalesAmount', label: 'Хотын татв. борл.', width: 140, render: renderWithLedgerHighlight('CityTaxSalesAmount', formatNumber) },
      { key: 'VATPayableAmount', label: 'Төлөх НӨАТ', width: 140, render: renderWithLedgerHighlight('VATPayableAmount', formatNumber) },
      { key: 'NonVATSalesAmount', label: 'НӨАТ-гүй борл.', width: 140, render: renderWithLedgerHighlight('NonVATSalesAmount', formatNumber) },
      { key: 'VATInclusiveSalesAmount', label: 'НӨАТ-тэй борл.', width: 140, render: renderWithLedgerHighlight('VATInclusiveSalesAmount', formatNumber) },
      { key: 'IsLedger', label: 'Дэвтэрт үү', width: 120, render: renderWithLedgerHighlight('IsLedger', (v) => (v ? 'Тийм' : 'Үгүй')) },
      { key: 'CreateDate', label: 'Үүсгэсэн огноо', width: 160, render: renderWithLedgerHighlight('CreateDate', formatDate) },
    ],
    []
  );

  const handleRefresh = () => fetchReceipts();

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Борлуулалтын баримт</h1>
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={() => setShowColumnSearch((prev) => !prev)}
            onColumnSettings={() => setShowColumnSettings((prev) => !prev)}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex flex-wrap gap-4 mb-6">
            {/* Customer Selection Dropdown */}
            <div className="flex flex-col relative customer-dropdown-container">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-customer-select">
                Харилцагч сонгох
              </label>
              <div className="relative">
                <input
                  id="sales-customer-select"
                  type="text"
                  className="border rounded px-3 py-2 w-64 pr-10 cursor-pointer"
                  value={selectedCustomerOid ? selectedCustomerName : customerSearchTerm}
                  onChange={(e) => {
                    setCustomerSearchTerm(e.target.value);
                    setShowCustomerDropdown(true);
                    if (!e.target.value) {
                      setSelectedCustomerOid('');
                    }
                  }}
                  onFocus={() => {
                    if (!selectedCustomerOid) {
                      setShowCustomerDropdown(true);
                    }
                  }}
                  placeholder="Харилцагч хайх..."
                  readOnly={!!selectedCustomerOid}
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                  {selectedCustomerOid ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomerOid('');
                        setCustomerSearchTerm('');
                        setShowCustomerDropdown(false);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
                {showCustomerDropdown && !selectedCustomerOid && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {loadingCustomers ? (
                      <div className="p-3 text-center text-gray-500">Уншиж байна...</div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="p-3 text-center text-gray-500">Харилцагч олдсонгүй</div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <div
                          key={customer.Oid}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setSelectedCustomerOid(customer.Oid);
                            setCustomerSearchTerm('');
                            setShowCustomerDropdown(false);
                          }}
                        >
                          <div className="font-medium text-gray-900">{customer.CustomerName}</div>
                          <div className="text-sm text-gray-500">{customer.CustomerID}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-customer-id">
                Регистр
              </label>
              <input
                id="sales-customer-id"
                type="text"
                className="border rounded px-3 py-2 w-48"
                value={customerIdFilter}
                onChange={(e) => setCustomerIdFilter(e.target.value)}
                placeholder="РД хайх"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-customer-name">
                Нэр
              </label>
              <input
                id="sales-customer-name"
                type="text"
                className="border rounded px-3 py-2 w-56"
                value={customerNameFilter}
                onChange={(e) => setCustomerNameFilter(e.target.value)}
                placeholder="Нэр хайх"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-year">
                Он (year)
              </label>
              <input
                id="sales-year"
                type="number"
                className="border rounded px-3 py-2 w-32"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-period">
                Үе (period)
              </label>
              <input
                id="sales-period"
                type="number"
                className="border rounded px-3 py-2 w-32"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={fetchReceipts}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Шүүх
              </button>
            </div>
          </div>
        
        {error && (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center text-gray-500">Уншиж байна...</div>
          ) : (
            <SmartTable
              data={filteredReceipts}
              columns={columns}
              fetchData={fetchReceipts}
              settingsKey="sales-receipts"
              rowKey="Oid"
              editable={false}
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
              footerData={footerData}
            />
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default SalesReceiptsPage;
